import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  recetasAPI,
  type FiltrosRecetas,
  type ActualizarRecetaRequest,
  type AgregarIngredienteRequest,
  type ActualizarIngredienteRequest,
} from '../services/recetasAPI';

// Query Keys para cache management
export const recetasKeys = {
  all: ['recetas'] as const,
  lists: () => [...recetasKeys.all, 'list'] as const,
  list: (filters?: FiltrosRecetas) =>
    [...recetasKeys.lists(), { filters }] as const,
  details: () => [...recetasKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...recetasKeys.details(), id] as const,
  categorias: () => [...recetasKeys.all, 'categorias'] as const,
  tiposPlato: () => [...recetasKeys.all, 'tipos-plato'] as const,
};

// Hook para listar recetas con filtros y paginación
export function useRecetas(filtros?: FiltrosRecetas) {
  return useQuery({
    queryKey: recetasKeys.list(filtros),
    queryFn: () => recetasAPI.listar(filtros),
    keepPreviousData: true, // Mantener datos anteriores durante la carga
    staleTime: 0, // Siempre considerar datos como stale para refrescar con múltiples usuarios
    refetchOnMount: 'always', // Siempre refrescar al montar componente
  });
}

// Hook para obtener una receta específica
export function useReceta(id: string | number) {
  return useQuery({
    queryKey: recetasKeys.detail(id),
    queryFn: () => recetasAPI.obtener(id),
    enabled: !!id,
    staleTime: 0, // Siempre traer datos frescos para recetas individuales
    refetchOnMount: 'always', // Siempre refrescar al abrir modal de edición
  });
}

// Hook para obtener categorías de recetas (clasificación secundaria)
export function useCategorias() {
  return useQuery({
    queryKey: recetasKeys.categorias(),
    queryFn: recetasAPI.obtenerCategorias,
    staleTime: 1000 * 60 * 60, // 1 hora
  });
}

// Hook para obtener tipos de plato (clasificación principal)
export function useTiposPlato() {
  return useQuery({
    queryKey: recetasKeys.tiposPlato(),
    queryFn: recetasAPI.obtenerTiposPlato,
    staleTime: 1000 * 60 * 60, // 1 hora
  });
}

// Hook para obtener las recetas activas asociadas a un tipo de plato.
// Lo usa el wizard de cliente (paso 3) para mostrar el catálogo de BEBIDAS
// cuando el cliente activa el acceso a BEBIDA (id=11).
export function useRecetasPorTipoPlato(
  tipoPlatoId: number | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [...recetasKeys.all, 'por-tipo-plato', tipoPlatoId],
    queryFn: () =>
      tipoPlatoId === null
        ? Promise.resolve([])
        : recetasAPI.obtenerRecetasPorTipoPlato(tipoPlatoId),
    enabled: tipoPlatoId !== null && options?.enabled !== false,
    staleTime: 1000 * 60 * 10, // 10 minutos
  });
}

// Hook para obtener todas las recetas para estadísticas (sin paginación)
export function useRecetasEstadisticas() {
  return useQuery({
    queryKey: [...recetasKeys.all, 'estadisticas'],
    queryFn: () => recetasAPI.listar({ page: 1, limit: 9999 }), // Obtener todas las recetas
    // Fix tanda1 H4 (2026-06-30): el staleTime era 5 min → las
    // tarjetas de conteo (Total / Disponibles / Borradores /
    // Archivadas) no reflejaban el archivado/restaurado instantáneo
    // aunque el hook de mutación invalidara esta query (la data se
    // quedaba "fresh" según React Query). Lo bajamos a 0 para que
    // cualquier invalidate dispare refetch real.
    staleTime: 0,
    select: (data) => data?.data || [], // Solo nos interesa el array de recetas
  });
}

// Hook para crear receta
export function useCrearReceta() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => {
      console.log('🔥 useCrearReceta - Datos recibidos:', data);
      return recetasAPI.crear(data);
    },
    onSuccess: async (nuevaReceta) => {
      console.log(
        '✅ useCrearReceta - Receta creada exitosamente:',
        nuevaReceta
      );

      // Invalidar y refetch de listas de recetas (fuerza actualización inmediata)
      await queryClient.invalidateQueries({
        queryKey: recetasKeys.lists(),
        refetchType: 'active', // Refetch inmediato de queries activas
      });
      console.log('🔄 Cache invalidado y refrescado para listas de recetas');

      // Agregar a cache de detalle
      queryClient.setQueryData(recetasKeys.detail(nuevaReceta.id), nuevaReceta);
      console.log('💾 Receta agregada al cache de detalles');

      // Invalidar estadísticas también
      await queryClient.invalidateQueries({
        queryKey: [...recetasKeys.all, 'estadisticas'],
        refetchType: 'active',
      });
    },
    onError: (error: any) => {
      console.error('❌ Error creando receta:', error);
      console.error('❌ Respuesta del servidor:', error.response?.data);
      console.error('❌ Status:', error.response?.status);
    },
  });
}

// Hook para actualizar receta
export function useActualizarReceta() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string | number;
      data: ActualizarRecetaRequest;
    }) => recetasAPI.actualizar(id, data),
    onSuccess: async (recetaActualizada, variables) => {
      // Invalidar y refetch de listas de recetas (fuerza actualización inmediata)
      await queryClient.invalidateQueries({
        queryKey: recetasKeys.lists(),
        refetchType: 'active', // Refetch inmediato de queries activas
      });

      // Actualizar cache de detalle con los datos actualizados
      queryClient.setQueryData(
        recetasKeys.detail(variables.id),
        recetaActualizada
      );

      // Invalidar estadísticas también
      // Fix tanda1 H4 (2026-06-30): refetchType: 'all' incluye queries
      // inactivas (la página de estadísticas puede no estar visible si
      // el usuario está dentro de un modal, igual hay que refrescar
      // las tarjetas que cuentan disponibles/archivadas).
      await queryClient.invalidateQueries({
        queryKey: [...recetasKeys.all, 'estadisticas'],
        refetchType: 'all',
      });

      console.log('✅ Receta actualizada y cache refrescado');
    },
    onError: (error: any) => {
      console.error('❌ Error actualizando receta:', error);
    },
  });
}

// Hook para eliminar/archivar receta
export function useEliminarReceta() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: recetasAPI.eliminar,
    onSuccess: async (_, id) => {
      // Invalidar y refetch de listas de recetas (fuerza actualización inmediata)
      await queryClient.invalidateQueries({
        queryKey: recetasKeys.lists(),
        refetchType: 'active',
      });

      // Remover de cache de detalle
      queryClient.removeQueries({ queryKey: recetasKeys.detail(id) });

      // Invalidar estadísticas también
      await queryClient.invalidateQueries({
        queryKey: [...recetasKeys.all, 'estadisticas'],
        refetchType: 'active',
      });

      console.log('✅ Receta archivada y cache refrescado');
    },
    onError: (error: any) => {
      console.error('❌ Error archivando receta:', error);
    },
  });
}

// Hook para agregar ingrediente a receta
export function useAgregarIngrediente() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recetaId,
      data,
    }: {
      recetaId: string | number;
      data: AgregarIngredienteRequest;
    }) => recetasAPI.agregarIngrediente(recetaId, data),
    onSuccess: (_, variables) => {
      // Invalidar lista de recetas
      queryClient.invalidateQueries({ queryKey: recetasKeys.lists() });

      // Invalidar receta específica para refrescar ingredientes
      queryClient.invalidateQueries({
        queryKey: recetasKeys.detail(variables.recetaId),
      });

      console.log('Ingrediente agregado exitosamente');
    },
    onError: (error: any) => {
      console.error('Error agregando ingrediente:', error);
    },
  });
}

// Hook para actualizar ingrediente en receta
export function useActualizarIngrediente() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recetaId,
      ingredienteId,
      data,
    }: {
      recetaId: string | number;
      ingredienteId: string | number;
      data: ActualizarIngredienteRequest;
    }) => recetasAPI.actualizarIngrediente(recetaId, ingredienteId, data),
    onSuccess: (_, variables) => {
      // Invalidar lista de recetas
      queryClient.invalidateQueries({ queryKey: recetasKeys.lists() });

      // Invalidar receta específica para refrescar ingredientes
      queryClient.invalidateQueries({
        queryKey: recetasKeys.detail(variables.recetaId),
      });

      console.log('Ingrediente actualizado exitosamente');
    },
    onError: (error: any) => {
      console.error('Error actualizando ingrediente:', error);
    },
  });
}

// Hook para remover ingrediente de receta
export function useRemoverIngrediente() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recetaId,
      ingredienteId,
    }: {
      recetaId: string | number;
      ingredienteId: string | number;
    }) => recetasAPI.removerIngrediente(recetaId, ingredienteId),
    onSuccess: (_, variables) => {
      // Invalidar lista de recetas
      queryClient.invalidateQueries({ queryKey: recetasKeys.lists() });

      // Invalidar receta específica para refrescar ingredientes
      queryClient.invalidateQueries({
        queryKey: recetasKeys.detail(variables.recetaId),
      });

      console.log('Ingrediente removido exitosamente');
    },
    onError: (error: any) => {
      console.error('Error removiendo ingrediente:', error);
    },
  });
}

// Hook para incrementar cantidad de ingrediente en receta
export function useIncrementarCantidadIngrediente() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recetaId,
      ingredienteRecetaId,
      cantidadAdicional,
    }: {
      recetaId: string | number;
      ingredienteRecetaId: number;
      cantidadAdicional: number;
    }) =>
      recetasAPI.incrementarCantidadIngrediente(
        recetaId,
        ingredienteRecetaId,
        cantidadAdicional
      ),
    onSuccess: (_, variables) => {
      // Invalidar lista de recetas
      queryClient.invalidateQueries({ queryKey: recetasKeys.lists() });

      // Invalidar receta específica para refrescar ingredientes
      queryClient.invalidateQueries({
        queryKey: recetasKeys.detail(variables.recetaId),
      });

      console.log('Cantidad de ingrediente incrementada exitosamente');
    },
    onError: (error: any) => {
      console.error('Error incrementando cantidad de ingrediente:', error);
    },
  });
}

// Hook personalizado para manejo completo de recetas con estado local
export function useRecetasManager() {
  const queryClient = useQueryClient();

  // Mutations
  const crearMutation = useCrearReceta();
  const actualizarMutation = useActualizarReceta();
  const eliminarMutation = useEliminarReceta();
  const agregarIngredienteMutation = useAgregarIngrediente();
  const actualizarIngredienteMutation = useActualizarIngrediente();
  const removerIngredienteMutation = useRemoverIngrediente();

  // Funciones de conveniencia
  const invalidarRecetas = () => {
    queryClient.invalidateQueries({ queryKey: recetasKeys.lists() });
  };

  const invalidarReceta = (id: string | number) => {
    queryClient.invalidateQueries({ queryKey: recetasKeys.detail(id) });
  };

  const prefetchReceta = (id: string | number) => {
    queryClient.prefetchQuery({
      queryKey: recetasKeys.detail(id),
      queryFn: () => recetasAPI.obtener(id),
      staleTime: 1000 * 60 * 10,
    });
  };

  return {
    // Mutations
    crear: crearMutation.mutate,
    actualizar: actualizarMutation.mutate,
    eliminar: eliminarMutation.mutate,
    agregarIngrediente: agregarIngredienteMutation.mutate,
    actualizarIngrediente: actualizarIngredienteMutation.mutate,
    removerIngrediente: removerIngredienteMutation.mutate,

    // Estados de loading
    isCreating: crearMutation.isPending,
    isUpdating: actualizarMutation.isPending,
    isDeleting: eliminarMutation.isPending,
    isManagingIngredients:
      agregarIngredienteMutation.isPending ||
      actualizarIngredienteMutation.isPending ||
      removerIngredienteMutation.isPending,

    // Utilidades
    invalidarRecetas,
    invalidarReceta,
    prefetchReceta,
  };
}
